// The two decisions inside the header's item search that are arithmetic rather
// than rendering: where an arrow key moves the highlight, and how an item's name
// reaches the URL. They live here because this project's vitest setup has no
// React renderer, so anything left inside the component cannot be tested at all.

/** Where ArrowDown (direction 1) and ArrowUp (-1) move the highlight, wrapping
 *  at both ends.
 *
 *  -1 is "nothing highlighted" — the state the list opens in and returns to on
 *  every new query — and it is deliberately not the last index: from there Down
 *  takes the first result and Up takes the last, which is what a reader expects
 *  from a list they have not entered yet.
 *
 *  An empty list stays at -1. Writing this as a bare `(current + direction) %
 *  length` returns NaN when length is 0, and that NaN becomes an
 *  aria-activedescendant pointing at an element id that does not exist, which
 *  screen readers follow into silence rather than reporting as an error. */
export function nextIndex(current: number, length: number, direction: 1 | -1): number {
  if (length <= 0) return -1;
  if (current < 0) return direction === 1 ? 0 : length - 1;
  return (current + direction + length) % length;
}

/** Every link to an item page is built here — the table's item column and the
 *  search dropdown both — so the two cannot drift apart on encoding.
 *
 *  The name rides in the query string because the API has no id-to-name route
 *  and the item page needs a heading before its first request returns. It has to
 *  be encoded: broker items are named things like "Soulshot: D & Spiritshot: D",
 *  and a raw `&` ends the name parameter early and loses the rest of it. */
export function itemHref(id: number, name: string): string {
  return `/items/${id}?name=${encodeURIComponent(name)}`;
}

/** Every link to a trader page is built here, for the same reason itemHref
 *  exists: a nick is other players' in-game name read by OCR, so it carries
 *  whatever punctuation they chose. Pasted into a path raw, a '#' truncates it
 *  at the fragment and the page opens on the wrong trader. */
export function traderHref(nick: string): string {
  return `/traders/${encodeURIComponent(nick)}`;
}

/** Every link to a recipe page is built here, beside itemHref and traderHref.
 *  A recipe id is a number from the catalogue, so nothing needs encoding — but
 *  the address is still built in one place, so a change of route shape is one
 *  edit rather than a search. */
export function recipeHref(id: number): string {
  return `/recipes/${id}`;
}
